import { Box, Chip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { green, red, grey, orange } from "@mui/material/colors";
import { EntityDataModel } from "./cleaning/types";
import {
  ClaimType,
  RiskAssessmentResult,
} from "./search-results/types/risk-search-response";

export const DEFAULT_YEAR = 1000;

export const renderStatus = (status: string) => {
  const colors: {
    [index: string]:
      | "success"
      | "error"
      | "default"
      | "warning"
      | "info"
      | "primary"
      | "secondary";
  } = {
    Imported: "default",
    Uploaded: "info",
    Mapped: "primary",
    Cleansing: "warning",
    Screening: "secondary",
    Screened: "success",
    Error: "error",
  };

  return <Chip label={status} color={colors[status]} size="small" />;
};

export const renderTaskStatus = (status: string) => {
  const colors: {
    [index: string]:
      | "success"
      | "error"
      | "default"
      | "warning"
      | "info"
      | "primary"
      | "secondary";
  } = {
    Requested: "primary",
    Processing: "info",
    Completed: "success",
    Cancelled: "warning",
    Failed: "error",
    NotImplemented: "default",
  };

  return (
    <Chip
      label={status}
      color={colors[status.replace(/\s+/g, "")]}
      size="small"
    />
  );
};

export const renderDate = (customDate: string, short?: boolean) => {
  // Parse ISO date string as local time, not UTC
  // JavaScript treats "2024-05-12T14:50:20" as UTC, so we need to manually parse it
  let dateObj: Date;

  if (customDate.includes("T")) {
    // ISO format - parse manually to treat as local time
    const [datePart, timePart] = customDate.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes, secondsWithMs] = timePart.split(":");
    const seconds = parseFloat(secondsWithMs);

    dateObj = new Date(
      year,
      month - 1,
      day,
      Number.parseInt(hours, 10),
      Number.parseInt(minutes, 10),
      Math.floor(seconds),
    );
  } else {
    dateObj = new Date(customDate);
  }

  if (
    dateObj.getFullYear() < DEFAULT_YEAR ||
    dateObj.getFullYear() > new Date().getFullYear()
  ) {
    return null;
  }

  const datePart = dateObj.toLocaleString("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  if (short) return datePart;

  const formatter = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return formatter.format(dateObj);
};

export const renderDateWithoutTime = (customDate: string) => {
  const dateObj = new Date(customDate);

  if (
    dateObj.getFullYear() < DEFAULT_YEAR ||
    dateObj.getFullYear() > new Date().getFullYear()
  ) {
    return null;
  }

  return dateObj.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const renderStatusIcon = (value: boolean) => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      width="100%"
      height="100%"
      padding={2}
    >
      {value ? (
        <CheckIcon sx={{ color: green[600] }} />
      ) : (
        <CloseRoundedIcon sx={{ color: red[600] }} />
      )}
    </Box>
  );
};

export const renderClaimTypeIcon = (value: ClaimType) => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      width="100%"
      height="100%"
      padding={1.5}
    >
      {value === ClaimType.Motor ? (
        <DirectionsCarIcon sx={{ color: grey[600] }} />
      ) : (
        <QuestionMarkIcon sx={{ color: grey[600] }} />
      )}
    </Box>
  );
};

export const renderRiskBadge = (risk: RiskAssessmentResult) => {
  switch (risk) {
    case "Green":
      return (
        <Chip
          label="Low Risk"
          sx={{
            backgroundColor: green[100],
            color: green[800],
            fontWeight: 600,
            borderRadius: "4px",
          }}
        />
      );
    case "Amber":
      return (
        <Chip
          label="Medium Risk"
          sx={{
            backgroundColor: orange[100],
            color: orange[800],
            fontWeight: 600,

            borderRadius: "4px",
          }}
        />
      );
    case "Red":
      return (
        <Chip
          label="High Risk"
          sx={{
            backgroundColor: red[100],
            color: red[800],
            fontWeight: 600,
            borderRadius: "4px",
          }}
        />
      );
    default:
      return null;
  }
};

export const getTotalFields = (entityData: EntityDataModel[] | undefined) => {
  let totalCount = 0;

  entityData?.forEach((entity) => {
    entity.fieldData.forEach((field) => {
      totalCount += field.count;
    });
  });

  return totalCount;
};

export const formatStringWithSpace = (str: string) => {
  if (str.length > 18) {
    const spaceIndex = str.lastIndexOf(" ", 18);

    if (spaceIndex !== -1) {
      return `${str.slice(0, spaceIndex)}\n${str.slice(spaceIndex + 1)}`;
    }
  }

  return str;
};

export const formatFieldName = (fieldName: string) => {
  if (typeof fieldName === "string")
    return fieldName.toString().replace(/([a-z])([A-Z])/g, "$1 $2");

  return fieldName;
};

export const chartPalette = [
  "#ccebc5",
  "#2b8cbe",
  "#7bccc4",
  "#a8ddb5",
  "#4eb3d3",
  "#08589e",
  "#99d8c9",
  "#5ab1d2",
  "#80d6d4",
  "#66b2b2",
  "#6bb7d4",
  "#74c5d1",
  "#58a8b7",
  "#73c7d0",
  "#4fa6c7",
];

export const getRandomColor = () => {
  const randomIndex = Math.floor(Math.random() * chartPalette.length);
  return chartPalette[randomIndex];
};

export function valueFormatter(value: number | null) {
  return `Count: ${value}`;
}

export const normalizeString = (value: string) =>
  value.replaceAll(/\s+/g, "").toUpperCase();

export const toTitleCase = (text: string): string => {
  if (!text) return "";

  const spaced = text.replaceAll(/[-_]+/g, " ");

  return spaced
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const toKebabCase = (str: string) =>
  str
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase()
    .replace(/s$/, "");
